import { useEffect, useState } from 'react';
import { useUIStore } from '@/state/store';
import type { PeerStats, QualityLevel } from '@/hooks/useWebRtc';

interface SectionProps {
  id: string;
  label: React.ReactNode;
  state?: React.ReactNode;
  badge?: React.ReactNode;
  showToggle?: boolean;
  children: React.ReactNode;
}

function Section({ id, label, state, badge, showToggle = true, children }: SectionProps) {
  const collapsed = useUIStore((s) => s.sectionsCollapsed[id] ?? false);
  const toggle = useUIStore((s) => s.toggleSection);

  return (
    <div className={`sp-section ${collapsed ? 'collapsed' : ''}`} id={id}>
      <div className="sp-section-head" onClick={() => toggle(id)}>
        <div className="sp-label">
          {label}
          {badge && <span className="sp-badge">{badge}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {state}
          {showToggle && (
            <button className="sp-toggle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="sp-body">{children}</div>
    </div>
  );
}

function CustomerSection() {
  const incoming = useUIStore((s) => s.incomingCall);
  const steleLine = [incoming?.steleName, incoming?.steleLocation].filter(Boolean).join(' · ') || 'Stele';
  return (
    <Section id="sec-customer" label="Kunde">
      <div className="cust-row">
        <div className="cust-avatar">★</div>
        <div className="cust-info">
          <div className="cust-name">Gast</div>
          <div className="cust-loc">{steleLine}</div>
        </div>
      </div>
      <NfcBox />
    </Section>
  );
}

export function NfcBox() {
  const nfcData = useUIStore((s) => s.nfcData);
  const nfcReceivedAt = useUIStore((s) => s.nfcReceivedAt);
  const clearNfcData = useUIStore((s) => s.clearNfcData);
  const [now, setNow] = useState(Date.now());

  // Auto-hide nach 60s
  useEffect(() => {
    if (!nfcReceivedAt) return;
    const id = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n - nfcReceivedAt > 60_000) {
        clearNfcData();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [nfcReceivedAt, clearNfcData]);

  if (!nfcData) return null;
  if (!nfcData.success) {
    return (
      <div className="nfc-box" style={{ borderColor: 'rgba(239,68,68,0.35)' }}>
        <div className="nfc-box-head">
          <div className="nfc-box-label">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
            EC-Karte
          </div>
        </div>
        <div className="nfc-rows-compact">
          <div className="nfc-row-c" style={{ color: '#fca5a5' }}>
            <span className="k">Status</span>
            <span className="v">Lesen fehlgeschlagen</span>
          </div>
        </div>
      </div>
    );
  }

  const remaining = nfcReceivedAt ? Math.max(0, 60 - Math.floor((now - nfcReceivedAt) / 1000)) : 0;

  return (
    <div className="nfc-box">
      <div className="nfc-box-head">
        <div className="nfc-box-label">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
          </svg>
          EC-Karte
        </div>
        <div className="nfc-box-timer">Auto-Hide {remaining}s</div>
      </div>
      <div className="nfc-rows-compact">
        {nfcData.pan && <NfcRow k="PAN" v={nfcData.pan} />}
        {nfcData.bancName && <NfcRow k="Bank" v={nfcData.bancName} />}
        {nfcData.blz && <NfcRow k="BLZ" v={nfcData.blz} />}
        {nfcData.bic && <NfcRow k="BIC" v={nfcData.bic} />}
        {nfcData.expirationDate && <NfcRow k="Gültig" v={nfcData.expirationDate} />}
        {nfcData.accountNumber && <NfcRow k="Konto" v={nfcData.accountNumber} />}
        {nfcData.chipCardNumber && <NfcRow k="Chip-Nr." v={nfcData.chipCardNumber} />}
        {nfcData.bancLocation && <NfcRow k="Ort" v={nfcData.bancLocation} />}
      </div>
    </div>
  );
}

function NfcRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="nfc-row-c">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/** Welle 8c-pre32: ein RTT-Verlaufsgraph JE Seite (eigene min/max). RTT ist der
 *  Roundtrip, den diese Seite misst — bei gesunder, symmetrischer Strecke laufen beide
 *  Graphen ähnlich; divergieren sie, ist die Strecke einer Seite das Problem. */
function MiniSpark({ history, level }: { history: number[]; level: QualityLevel }) {
  const color = level === 'bad' ? 'var(--danger)' : level === 'fair' ? 'var(--warning)' : 'var(--status-available)';
  if (history.length < 2) {
    return <div className="ts2-spark ts2-spark-empty">RTT · sammelt Daten…</div>;
  }
  const min = Math.min(...history);
  const max = Math.max(...history);
  const range = max - min || 1;
  const step = 240 / (history.length - 1);
  const pts = history
    .map((v, i) => `${(i * step).toFixed(1)},${(28 - ((v - min) / range) * 24).toFixed(1)}`)
    .join(' ');
  return (
    <div className="ts2-spark">
      <div className="ts2-spark-head">
        <span>RTT · {history.length * 3}s</span>
        <span>min {Math.round(min)} · max {Math.round(max)} ms</span>
      </div>
      <svg viewBox="0 0 240 28" preserveAspectRatio="none">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
      </svg>
    </div>
  );
}

function qualWorst(a: PeerStats | null, b: PeerStats | null): QualityLevel {
  const ls = [a?.level, b?.level].filter(Boolean) as QualityLevel[];
  if (ls.includes('bad')) return 'bad';
  if (ls.includes('fair')) return 'fair';
  return 'good';
}

/** Welle 8c-pre32: Block EINER Seite. Zeigt die drei Netz-Kennzahlen, die diese Seite
 *  beim EMPFANG misst (RTT/Jitter/Verlust), unsere Kennzahlen (Auflösung/fps/Codec/
 *  Freezes) und den seiteneigenen RTT-Graphen. */
function SideBlock({
  name, hint, s, history,
}: { name: string; hint: string; s: PeerStats | null; history: number[] }) {
  const dot = !s ? '' : s.level === 'bad' ? 'bad' : s.level === 'fair' ? 'warn' : 'ok';
  const rttCls = s && s.rttMs != null && s.rttMs > 500 ? 'bad' : s && s.rttMs != null && s.rttMs > 250 ? 'warn' : '';
  const jitCls = s && s.jitterMs != null && s.jitterMs > 50 ? 'bad' : s && s.jitterMs != null && s.jitterMs > 30 ? 'warn' : '';
  const lossCls = s && s.lossPct >= 3 ? 'bad' : s && s.lossPct >= 1 ? 'warn' : '';
  return (
    <div className="ts2-side">
      <div className="ts2-head">
        <span className={`ts2-dot ${dot}`}></span>
        <span className="ts2-name">{name}</span>
        <span className="ts2-hint">{hint}</span>
      </div>
      {s ? (
        <>
          <div className="ts2-metrics">
            <div className="ts2-m"><div className="l">RTT</div><div className={`v ${rttCls}`}>{s.rttMs ?? '—'}<small>ms</small></div></div>
            <div className="ts2-m"><div className="l">Jitter</div><div className={`v ${jitCls}`}>{s.jitterMs ?? '—'}<small>ms</small></div></div>
            <div className="ts2-m"><div className="l">Verlust</div><div className={`v ${lossCls}`}>{s.lossPct}<small>%</small></div></div>
          </div>
          <div className="ts2-our">{s.width}×{s.height} · {s.rxFps} fps · {s.codec} · Freezes {s.freezeCount}</div>
          <MiniSpark history={history} level={s.level} />
        </>
      ) : (
        <div className="ts2-wait">— wartet auf Daten —</div>
      )}
    </div>
  );
}

function TelemetrySection() {
  // Welle 8c-pre32: echte Werte JE Seite. agent = was der Berater empfängt (Gast-Video),
  // stele = was die Stele empfängt (Berater-Video). RTT/Jitter/Verlust sind die vom
  // jeweiligen Empfänger gemessenen Netz-Kennzahlen; ein Stele-seitiges Problem wird so
  // als solches sichtbar. Jede Seite hat ihren eigenen RTT-Verlaufsgraphen.
  const agent = useUIStore((s) => s.callStatsAgent);
  const stele = useUIStore((s) => s.callStatsStele);
  const rttHistoryAgent = useUIStore((s) => s.rttHistoryAgent);
  const rttHistoryStele = useUIStore((s) => s.rttHistoryStele);

  const worst = qualWorst(agent, stele);
  const stateCls = worst === 'bad' ? 'bad' : worst === 'fair' ? 'warn' : '';
  const stateLabel = worst === 'bad' ? 'Schwach' : worst === 'fair' ? 'Eingeschränkt' : 'Gut';
  const relay = !!(agent?.relay || stele?.relay);

  return (
    <Section
      id="sec-telemetry"
      label="Verbindungsqualität"
      showToggle={true}
      state={
        <div className={`sp-state ${stateCls}`}>
          <span className="ts-dot"></span>
          {stateLabel}
        </div>
      }
    >
      <SideBlock name="Berater" hint="empfängt Gast" s={agent} history={rttHistoryAgent} />
      <SideBlock name="Stele" hint="empfängt Berater" s={stele} history={rttHistoryStele} />

      <div className={`tel-loc-compact ${relay ? 'warn' : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
        <span>{relay ? 'TURN-Relay aktiv' : 'Direktverbindung (P2P)'}</span>
      </div>
    </Section>
  );
}

function Cell({ l, v, small }: { l: string; v: string; small: string }) {
  return (
    <div className="tel-cell-c">
      <div className="l">{l}</div>
      <div className="v">
        {v}
        <small>{small}</small>
      </div>
    </div>
  );
}

function TranslationSection() {
  const translationOn = useUIStore((s) => s.translationOn);
  const capSizeCustomer = useUIStore((s) => s.capSizeCustomer);
  const capSizeSelf = useUIStore((s) => s.capSizeSelf);
  const capWidthPercent = useUIStore((s) => s.capWidthPercent);
  const setCapSize = useUIStore((s) => s.setCapSize);
  const setCapWidth = useUIStore((s) => s.setCapWidth);

  return (
    <Section
      id="sec-translation"
      label="Übersetzung"
      showToggle={true}
      state={
        <div className={`sp-state ${translationOn ? '' : 'warn'}`}>
          <span className="ts-dot"></span>
          {translationOn ? 'Aktiv · TR ↔ DE' : 'Inaktiv'}
        </div>
      }
    >
      <div className="tx-block-compact">
        <div className="tx-lang-compact">
          <span className="tx-side">Du</span>
          <div className="tx-pick">
            <span>🇩🇪 Deutsch</span>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>
        <div className="tx-lang-compact">
          <span className="tx-side">Stele</span>
          <div className="tx-pick">
            <span>🇹🇷 Türkçe</span>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>
        <button className="tx-action-compact">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          Sprachauswahl beim Kunden öffnen
        </button>
      </div>

      <div className="tx-cap-sizes">
        <SizeRow label="Kunde" min={16} max={36} value={capSizeCustomer} onChange={(v) => setCapSize('customer', v)} unit="" />
        <SizeRow label="Du" min={11} max={22} value={capSizeSelf} onChange={(v) => setCapSize('self', v)} unit="" />
        <SizeRow label="Breite" min={40} max={92} value={capWidthPercent} onChange={setCapWidth} unit="%" />
      </div>

      <div style={{ marginTop: 8 }}>
        <textarea className="tx-input-compact" placeholder="Nachricht an Kunde (auto-übersetzt)…"></textarea>
        <button className="tx-send-compact">Senden → 🇹🇷</button>
      </div>
    </Section>
  );
}

function SizeRow({
  label,
  min,
  max,
  value,
  onChange,
  unit,
}: {
  label: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
  unit: string;
}) {
  return (
    <div className="tx-cap-size-row">
      <span className="label">{label}</span>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="val">
        {value}
        {unit}
      </span>
    </div>
  );
}

export function DocsSection() {
  const docs = useUIStore((s) => s.sessionDocs);
  const sorted = [...docs].sort((a, b) => b.at - a.at);

  function openDoc(url?: string) {
    if (url) window.open(url, '_blank', 'noopener');
  }
  function iconFor(kind: string) {
    return kind === 'signed' ? 'SIG' : kind === 'nfc' ? 'EC' : 'SCAN';
  }

  return (
    <Section id="sec-docs" label="Session-Ablage" badge={sorted.length}>
      <div className="docs-compact-head" style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '0.05em' }}>
          Signierte PDFs · Scans · EC-Karten-Protokolle
        </span>
      </div>
      {sorted.length === 0 ? (
        <div className="docs-empty" style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 2px' }}>
          Noch keine Dokumente in dieser Session.
        </div>
      ) : (
        <div className="docs-list-compact" id="docs-list">
          {sorted.map((d) => (
            <div key={d.id} className="doc-item-c">
              <div className={`doc-icon-c ${d.kind}`}>{iconFor(d.kind)}</div>
              <div className="doc-meta-c">
                <div className="doc-name-c">{d.name}</div>
                <div className={`doc-status-c ${d.kind === 'signed' ? 'signed' : ''}`}>{d.status}</div>
                {d.kind === 'nfc' && d.data && (
                  <div className="doc-nfc-fields" style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(d.data).map(([k, v]) => (
                      <div key={k} style={{ fontSize: 10, color: 'var(--muted)' }}>
                        <span style={{ opacity: 0.7 }}>{k}:</span> {v}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {d.url && (
                <div className="doc-actions-c">
                  <button className="doc-action-c" title="Öffnen" onClick={() => openDoc(d.url)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export function SidePanel() {
  return (
    <aside className="side-panel" id="side-panel">
      <TranslationSection />
    </aside>
  );
}
